import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, CircularProgress,
  Popover, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import {
  Email as EmailIcon, ExpandMore as ExpandMoreIcon,
  Star as StarIcon, StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { supabase } from '../../utils/supabaseClient';

const EmailDisplayCell = ({ providerId }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (providerId) {
      fetchEmails();
    }
  }, [providerId]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      
      // Get auth token
      const { data: authData } = await supabase.auth.getSession();
      const token = authData?.session?.access_token;

      if (!token) {
        console.error('No authentication token available');
        return;
      }
      
      const response = await fetch(`/api/provider-emails/${providerId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setEmails(data.emails);
      } else {
        console.error('Failed to fetch emails:', data.message);
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    if (emails.length > 1) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'email-popover' : undefined;

  if (loading) {
    return <CircularProgress size={16} />;
  }

  if (emails.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No emails
      </Typography>
    );
  }

  if (emails.length === 1) {
    const email = emails[0];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {email.is_primary ? (
          <StarIcon color="warning" fontSize="small" />
        ) : (
          <EmailIcon color="action" fontSize="small" />
        )}
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {email.email}
        </Typography>
      </Box>
    );
  }

  const primaryEmail = emails.find(e => e.is_primary) || emails[0];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
          borderRadius: 1,
          p: 0.5
        }}
        onClick={handleClick}
      >
        <StarIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {primaryEmail.email}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            +{emails.length - 1} more
          </Typography>
        </Box>
        <ExpandMoreIcon fontSize="small" color="action" />
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { maxWidth: 300, maxHeight: 200 }
        }}
      >
        <List dense>
          {emails.map((email) => (
            <ListItem key={email.id}>
              <ListItemIcon>
                {email.is_primary ? (
                  <StarIcon color="warning" fontSize="small" />
                ) : (
                  <StarBorderIcon color="action" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={email.email}
                secondary={
                  <Chip
                    label={email.email_type}
                    size="small"
                    variant="outlined"
                    color={email.is_primary ? 'primary' : 'default'}
                  />
                }
              />
            </ListItem>
          ))}
        </List>
      </Popover>
    </Box>
  );
};

export default EmailDisplayCell; 